/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect } from 'react';
import {
    Box,
    Button,
    FlexCol,
    FlexRow,
    Heading,
    Icon,
    Radio,
    RadioGroupWithValidation,
    Select,
    SelectOption,
    Stack,
    Text,
    TextField,
} from 'sarsaparilla';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    changePassesByGuestType,
    getUpdatedOrderSummary,
} from '../../actions/orderDetailsActions';
import PassImages from './PassImages';
import '../../styles/DigitalPasses.scss';

function Passes({
    passesFromTour,
    passesFromReservation,
    guestTypeNameByGuestTypeId,
    ticketCountByGuestTypeId,
    addValidationRef,
    reservation,
}) {
    const dispatch = useDispatch();

    const passesFromRedux = useSelector(
        (state) => state.timedEntry.orderDetails?.passes_by_guest_type_id
    );

    const pricingViewData = useSelector((state) => state.timedEntry.pricingView);

    const { pricesByCategory } = pricingViewData;

    const allTourPrices = pricesByCategory?.INDIVIDUAL || [];

    const [pricing] = allTourPrices.filter(
        (item) => item.tour_id === reservation.tour_id
    );

    const reservationFee = pricing?.reservation_fee;

    const addOnFeeModelTour = reservationFee?.unit_price > 0;

    const isAddPassButtonVisible = (passesByGuestTypeId, ticketCount) =>
        ticketCount > passesByGuestTypeId;

    const getPassType = (pass) => {
        if (typeof pass === 'string') {
            return pass;
        }

        if (!pass || typeof pass !== 'object') {
            return '';
        }

        return pass.type || pass.pass_type || pass.value || '';
    };

    const isRadioChecked = (radioGroupValue, passes) =>
        passes.some(
            (pass) =>
                getPassType(radioGroupValue).toUpperCase() ===
                getPassType(pass).toUpperCase()
        );

    const normalizeReservationPasses = (passes = {}) =>
        Object.keys(passes).reduce((acc, guestType) => {
            const guestPasses = Array.isArray(passes[guestType]) ? passes[guestType] : [];
            acc[guestType] = guestPasses
                .map((pass) => {
                    if (typeof pass === 'string') {
                        return pass ? { type: pass } : null;
                    }

                    if (pass?.is_digital) {
                        return pass?.number
                            ? { is_digital: true, number: pass.number }
                            : { is_digital: true };
                    }

                    return pass?.type ? { type: pass.type } : null;
                })
                .filter(Boolean);
            return acc;
        }, {});

    const handleAddPassClick = (guestType) => {
        const passes = {
            ...passesFromRedux,
            [guestType]: [...(passesFromRedux[guestType] || []), ''],
        };

        dispatch(changePassesByGuestType(passes));
    };

    const handleRemovePassClick = (guestType, selectedPassKey) => {
        const passes = {
            ...passesFromRedux,
            [guestType]: [
                ...passesFromRedux[guestType].slice(0, selectedPassKey),
                ...passesFromRedux[guestType].slice(selectedPassKey + 1),
            ],
        };

        dispatch(changePassesByGuestType(passes));
        dispatch(getUpdatedOrderSummary(passes));
    };

    const handleSelectPass = (
        guestType,
        selectedPassKey,
        value,
        isDigitalNumber = false
    ) => {
        const passes = {
            ...passesFromRedux,
        };

        const selectedPassOption = passesFromTour[guestType]?.find(
            ({ is_digital }) => is_digital
        );
        const isDigitalPass = selectedPassOption?.is_digital && value === 'digital';

        let passValue;

        // User typing in the digital pass reservation number TextField
        if (isDigitalNumber) {
            passValue = {
                is_digital: true,
                number: value,
            };
        }

        // User selecting digital pass radio button
        if (isDigitalPass) {
            passValue = { is_digital: true };
        }

        // User selecting non-digital pass radio button or dropdown option
        if (!isDigitalNumber && !isDigitalPass) {
            passValue = { type: value };
        }

        passes[guestType][selectedPassKey] = passValue;

        dispatch(changePassesByGuestType(passes, true));

        // Skip order summary update when typing reservation number to avoid unnecessary API calls
        if (!isDigitalNumber) dispatch(getUpdatedOrderSummary(passes));
    };

    useEffect(() => {
        let passes = {};
        if (!isEmpty(passesFromReservation)) {
            passes = normalizeReservationPasses(passesFromReservation);
        }

        dispatch(changePassesByGuestType(passes));
        dispatch(getUpdatedOrderSummary(passes));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const passDisplayOrder = [
        'INTERAGENCY_RESIDENT_PASS',
        'INTERAGENCY_NONRESIDENT_PASS',
        'INTERAGENCY_ANNUAL_PASS',
        'INTERAGENCY_ANNUAL_SENIOR_PASS',
        'INTERAGENCY_LIFETIME_SENIOR_PASS',
        'INTERAGENCY_ACCESS_PASS',
        'INTERAGENCY_VOLUNTEER_PASS',
        'INTERAGENCY_MILITARY_PASS',
        'INTERAGENCY_LIFETIME_MILITARY_PASS',
        'INTERAGENCY_FOURTH_GRADE_PASS',
        'GOLDEN_AGE_PASS',
        'GOLDEN_ACCESS_PASS',
    ];

    const sortPasses = (passes) =>
        [...passes].sort((a, b) => {
            const aIdx = passDisplayOrder.indexOf(getPassType(a));
            const bIdx = passDisplayOrder.indexOf(getPassType(b));
            return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
        });

    const renderPass = (guestType, selectedPassKey) => {
        const passFromRedux = passesFromRedux[guestType];
        const passFromTour = passesFromTour[guestType];
        const radioGroupId = `pass-${guestType}-${selectedPassKey}`;
        const radioGroupValue = passFromRedux[selectedPassKey];

        // Find the selected pass option to check if it has multiple passes or is digital.
        const selectedPassOption = passFromTour.find(({ passes, is_digital }) =>
            is_digital
                ? radioGroupValue?.is_digital
                : isRadioChecked(radioGroupValue, passes || [])
        );
        const selectedRadioValue = selectedPassOption?.is_digital
            ? 'digital'
            : selectedPassOption?.passes?.length > 1
              ? getPassType(sortPasses(selectedPassOption.passes)[0])
              : getPassType(selectedPassOption?.passes?.[0]) ||
                radioGroupValue?.type ||
                '';
        const hasMultiplePasses = selectedPassOption?.passes?.length > 1;
        const isDigital = selectedPassOption?.is_digital;

        return (
            <>
                <RadioGroupWithValidation
                    ref={addValidationRef(10 + selectedPassKey * 2)}
                    label="Pass"
                    id={radioGroupId}
                    name={radioGroupId}
                    value={selectedRadioValue}
                    onChange={(e) =>
                        handleSelectPass(guestType, selectedPassKey, e.target.value)
                    }
                >
                    {passFromTour.map(({ label, passes, is_digital }, passOptionKey) => {
                        const radioId = `radio-${guestType}-${selectedPassKey}-${passOptionKey}`;
                        const radioValue = is_digital
                            ? 'digital'
                            : passes?.length > 1
                              ? getPassType(sortPasses(passes)[0])
                              : getPassType(passes?.[0]);

                        return (
                            <Radio
                                key={radioId}
                                id={radioId}
                                name={radioGroupId}
                                value={radioValue}
                                label={label}
                                className="mb-1"
                            />
                        );
                    })}
                </RadioGroupWithValidation>

                {hasMultiplePasses && (
                    <Stack>
                        <FlexRow>
                            <FlexCol md={12} lg={7} xl={8}>
                                <Select
                                    label="Pass Type"
                                    value={radioGroupValue?.type}
                                    placeholder="Select a pass type ..."
                                    onChange={(e) =>
                                        handleSelectPass(
                                            guestType,
                                            selectedPassKey,
                                            e.target.value
                                        )
                                    }
                                >
                                    {sortPasses(selectedPassOption.passes || []).map(
                                        (item) => (
                                            <SelectOption
                                                key={getPassType(item)}
                                                value={getPassType(item)}
                                            >
                                                {item.label}
                                            </SelectOption>
                                        )
                                    )}
                                </Select>
                            </FlexCol>
                            <FlexCol md={12} lg={5} xl={4}>
                                <PassImages type={radioGroupValue?.type} />
                            </FlexCol>
                        </FlexRow>
                    </Stack>
                )}

                {!hasMultiplePasses && !isDigital && radioGroupValue?.type && (
                    <PassImages type={radioGroupValue?.type} />
                )}

                {isDigital && (
                    <TextField
                        className="digital-pass-input"
                        label="Digital Pass Reservation Number"
                        placeholder="Enter your digital pass reservation number"
                        value={radioGroupValue?.number || ''}
                        onChange={(e) =>
                            handleSelectPass(
                                guestType,
                                selectedPassKey,
                                e.target.value,
                                true
                            )
                        }
                    />
                )}
            </>
        );
    };

    const renderPassesByGuestType = (guestType) => {
        const passes = passesFromRedux[guestType];
        const ticketCount = ticketCountByGuestTypeId[guestType];

        if (isEmpty(passes)) return null;

        return (
            <>
                {passes.map((selectedPass, selectedPassKey) => (
                    <Box
                        key={selectedPassKey}
                        padding="md"
                        background="gray-2"
                        border="gray"
                    >
                        <Heading headingLevel={4} appearance="small">
                            {ticketCount > 1
                                ? `Select Pass for Ticket ${
                                      selectedPassKey + 1
                                  } of ${ticketCount}`
                                : 'Select Pass'}
                        </Heading>

                        {renderPass(guestType, selectedPassKey)}

                        <div>
                            <Button
                                iconBeforeElement={<Icon iconName="delete" />}
                                appearance="link"
                                className="mt-1"
                                onClick={() =>
                                    handleRemovePassClick(guestType, selectedPassKey)
                                }
                            >
                                Remove Pass
                            </Button>
                        </div>
                    </Box>
                ))}
            </>
        );
    };

    const renderPassesForm = () => {
        const guestTypes = Object.keys(passesFromTour);
        return guestTypes.map((guestType) => {
            if (!ticketCountByGuestTypeId[guestType]) return null;

            return (
                <Box key={guestType} padding="md" background="gray-1" border="gray">
                    <Stack>
                        <Heading headingLevel={3} appearance="small">
                            {guestTypeNameByGuestTypeId[guestType]}
                        </Heading>
                        {renderPassesByGuestType(guestType)}
                        {isAddPassButtonVisible(
                            passesFromRedux[guestType]?.length || 0,
                            ticketCountByGuestTypeId[guestType]
                        ) && (
                            <div>
                                <Button
                                    appearance="secondary"
                                    iconBeforeElement={<Icon iconName="add-circle" />}
                                    onClick={() => handleAddPassClick(guestType)}
                                >
                                    Add a pass
                                </Button>
                            </div>
                        )}
                    </Stack>
                </Box>
            );
        });
    };

    return (
        <section>
            <Stack>
                <Heading headingLevel={3} appearance="h6">
                    Apply a Pass
                </Heading>

                {addOnFeeModelTour && (
                    <Text size="sm">
                        <strong>Note:</strong> Your annual or lifetime pass is accepted
                        for your entrance/standard amenity fee only. A non-refundable
                        reservation fee is required for each ticket. Individual pass
                        holders must present their passes to field agents upon check-in.
                        Depending on the site, you may be able to purchase a pass upon
                        arrival, please be prepared to pay for your pass with a credit
                        card.
                    </Text>
                )}

                {!addOnFeeModelTour && (
                    <Text size="sm">
                        <strong>Note:</strong> Your annual or lifetime pass is accepted
                        for your entrance/standard amenity fee only. Individual pass
                        holders must present their passes to field agents upon check-in.
                        Depending on the site, you may be able to purchase a pass upon
                        arrival, please be prepared to pay for your pass with a credit
                        card.
                    </Text>
                )}

                <Stack>{renderPassesForm()}</Stack>
            </Stack>
        </section>
    );
}

Passes.propTypes = {
    passesFromTour: PropTypes.object,
    passesFromReservation: PropTypes.object,
    guestTypeNameByGuestTypeId: PropTypes.object,
    ticketCountByGuestTypeId: PropTypes.object,
    addValidationRef: PropTypes.func,
    reservation: PropTypes.object,
};

export default Passes;
